import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import AccountBlockedNotice from "../components/auth/AccountBlockedNotice";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import PortalCodeEntry from "../components/portal/PortalCodeEntry";
import SocialLoginButtons from "../components/social-login/SocialLoginButtons";
import { API_BASE_URL } from "../config";
import {
  resetRequestLinkStatus,
  selectPortalRequestLinkStatus,
} from "../features/portal/portal-slice";
import { requestPortalLink } from "../features/portal/slice-extensions/portal-slice-extension";

/** Member-portal sign-in: email magic link / code, or Google / Microsoft / Apple. */
const PortalLoginPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const status = useAppSelector(selectPortalRequestLinkStatus);
  const [email, setEmail] = useState("");

  const ssoError = searchParams.get("error");

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim()) {
      dispatch(requestPortalLink(email.trim()));
    }
  };

  const handleProvider = (provider: "Google" | "Microsoft" | "Apple") => {
    window.location.assign(
      `${API_BASE_URL}/api/portal/auth/external?provider=${encodeURIComponent(provider)}`,
    );
  };

  return (
    <section className="mx-auto w-full max-w-md px-4 py-12">
      <h1 className="mb-2 text-2xl font-bold text-grey-20 dark:text-white">
        {t("portal.loginTitle")}
      </h1>
      <p className="mb-6 text-sm text-grey-40 dark:text-grey-80">
        {t(
          "portal.loginIntro",
          "See your schedule, balance and payments. Enter your email and we'll send you a sign-in link — no password needed.",
        )}
      </p>

      {ssoError === "account_blocked" && <AccountBlockedNotice />}
      {ssoError && ssoError !== "account_blocked" && (
        <p className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-300">
          {ssoError === "member-not-found"
            ? t(
                "portal.ssoMemberNotFound",
                "We couldn't find a member account for that email. Ask your instructor to add this address, or sign in with the email they have on file.",
              )
            : t("portal.ssoFailed", "Sign-in didn't complete. Please try again.")}
        </p>
      )}

      {status === "succeeded" ? (
        <div className="with-border rounded-2xl bg-white p-4 dark:bg-primary-20">
          <p className="font-medium text-grey-20 dark:text-white">
            {t("portal.linkSentTitle", "Check your email")}
          </p>
          <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
            {t(
              "portal.linkSentBodyWithCode",
              "If we know this address, an email is on its way. Click the sign-in link in it, or type the code below. Both work once and expire in 15 minutes.",
            )}
          </p>
          <PortalCodeEntry email={email.trim()} />
          <button
            type="button"
            className="btn btn-text mt-3"
            onClick={() => dispatch(resetRequestLinkStatus())}
          >
            {t("portal.useAnotherEmail", "Use another email")}
          </button>
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <label className="label">
              <span className="label-text">{t("portal.emailLabel", "Your email")}</span>
              <input
                type="email"
                required
                className="input"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
              />
            </label>
            <button type="submit" className="btn btn-primary" disabled={status === "loading"}>
              {status === "loading"
                ? t("portal.sending", "Sending…")
                : t("portal.sendLink", "Send sign-in link")}
            </button>
          </form>
          <SocialLoginButtons onProvider={handleProvider} />
        </>
      )}
    </section>
  );
};

export default PortalLoginPage;
